import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/contexts/CompanyContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  TrendingUp,
  Calendar,
  User,
  Maximize2,
  Minimize2,
  Link2Off,
} from 'lucide-react';
import type { TimeEntry } from '@/types';
import type { FinOpsTimesheetEntry } from '@/types/finops.types';
import { format } from 'date-fns';
import { ApprovalHistoryTimeline } from './approval-history-timeline';
import { fetchFinOpsTimesheets } from '@/lib/finops-api';
import { getDataMode } from '@/lib/data-service';

interface TimeEntriesTabProps {
  projectId: number;
  projectCode?: string;
  timeEntries: TimeEntry[];
  employees: Array<{ id: number; name: string }>;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

function mapFinOpsStatusToApproved(status: number): boolean {
  return status === 3;
}

function mapFinOpsTimesheetToTimeEntry(
  entry: FinOpsTimesheetEntry,
  projectId: number
): TimeEntry {
  const isApproved = mapFinOpsStatusToApproved(entry.status);
  return {
    id: parseInt(String(entry.primaryKey), 10) || 0,
    projectId,
    employeeId: parseInt(String(entry.platformUserId), 10) || 0,
    employeeName: entry.resourceName || undefined,
    date: entry.timesheetDate.split('T')[0],
    hours: entry.hours,
    description: entry.description || '',
    billable: !entry.isAbsence && !entry.isBooking,
    approved: isApproved,
    costRate: 0,
    billRate: 0,
    costCentreId: entry.costCentreCode,
    roleId: entry.roleCode,
    taskId: entry.taskPrimary > 0 ? entry.taskPrimary : undefined,
  };
}

type SortField = 'date' | 'employeeId' | 'hours' | 'description';
type SortDirection = 'asc' | 'desc';
type ApprovalFilter = 'all' | 'approved' | 'pending';
type BillableFilter = 'all' | 'billable' | 'non-billable';

interface TimeEntrySummary {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  totalCost: number;
  totalBillableValue: number;
  approvedHours: number;
  pendingHours: number;
  uniqueEmployees: number;
}

export function TimeEntriesTab({
  projectId,
  projectCode,
  timeEntries: propTimeEntries,
  employees,
  loading: propLoading = false,
  error: propError = null,
  onRetry,
}: TimeEntriesTabProps) {
  const { currentCompany } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState<string>('all');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('all');
  const [billableFilter, setBillableFilter] = useState<BillableFilter>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryEntry, setApprovalHistoryEntry] = useState<TimeEntry | null>(null);
  const [approvalHistoryExpanded, setApprovalHistoryExpanded] = useState(false);
  
  const [liveTimeEntries, setLiveTimeEntries] = useState<TimeEntry[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [isFinOpsMode, setIsFinOpsMode] = useState(false);
  const [finOpsNotConfigured, setFinOpsNotConfigured] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const dataMode = getDataMode();
      const companyInstanceId = currentCompany?.instanceId;
      
      if (dataMode === 'live' && projectCode) {
        if (companyInstanceId) {
          setIsFinOpsMode(true);
          setLiveLoading(true);
          setLiveError(null);
          setFinOpsNotConfigured(false);
          
          try {
            const finOpsData = await fetchFinOpsTimesheets(projectCode, companyInstanceId);
            if (finOpsData) {
              const mappedEntries = finOpsData.map((entry) => 
                mapFinOpsTimesheetToTimeEntry(entry, projectId)
              );
              setLiveTimeEntries(mappedEntries);
            } else {
              setLiveTimeEntries([]);
            }
          } catch (err) {
            // FinOps API unavailable (e.g. 401 when running locally without preprod auth)
            // Fall back gracefully to local database time entries (propTimeEntries)
            console.warn('[TimeEntriesTab] FinOps API unavailable, falling back to local data:', err);
            setIsFinOpsMode(false);
            setLiveTimeEntries([]);
          } finally {
            setLiveLoading(false);
          }
        } else {
          setFinOpsNotConfigured(true);
          setLiveTimeEntries([]);
          setIsFinOpsMode(false);
        }
      } else {
        setIsFinOpsMode(false);
        setFinOpsNotConfigured(false);
      }
    };

    loadData();
  }, [projectId, projectCode, currentCompany?.instanceId]);

  const timeEntries = isFinOpsMode ? liveTimeEntries : propTimeEntries;
  const loading = isFinOpsMode ? liveLoading : propLoading;
  const error = isFinOpsMode ? liveError : propError;

  const getEmployeeName = (employeeId: number, employeeName?: string): string => {
    if (employeeName) {
      return employeeName;
    }
    const employee = employees.find((e) => e.id === employeeId);
    return employee?.name || 'Unknown Employee';
  };

  // Get unique employees for filter
  const uniqueEmployees = useMemo(() => {
    if (isFinOpsMode) {
      const seen = new Map<string, number>();
      for (const e of timeEntries) {
        const name = e.employeeName || 'Unknown Employee';
        if (!seen.has(name)) {
          seen.set(name, e.employeeId);
        }
      }
      return Array.from(seen.entries())
        .map(([name, id]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const employeeIds = Array.from(new Set(timeEntries.map((e) => e.employeeId)));
    return employeeIds
      .map((id) => ({
        id,
        name: getEmployeeName(id),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [timeEntries, employees, isFinOpsMode]);

  // Calculate summary
  const summary: TimeEntrySummary = useMemo(() => {
    const totalHours = timeEntries.reduce((sum, e) => sum + e.hours, 0);
    const billableHours = timeEntries.filter((e) => e.billable).reduce((sum, e) => sum + e.hours, 0);
    const nonBillableHours = totalHours - billableHours;
    const totalCost = timeEntries.reduce((sum, e) => sum + e.hours * e.costRate, 0);
    const totalBillableValue = timeEntries
      .filter((e) => e.billable)
      .reduce((sum, e) => sum + e.hours * e.billRate, 0);
    const approvedHours = timeEntries.filter((e) => e.approved).reduce((sum, e) => sum + e.hours, 0);
    const pendingHours = timeEntries.filter((e) => !e.approved).reduce((sum, e) => sum + e.hours, 0);
    const uniqueEmployees = new Set(timeEntries.map((e) => e.employeeId)).size;

    return {
      totalHours,
      billableHours,
      nonBillableHours,
      totalCost,
      totalBillableValue,
      approvedHours,
      pendingHours,
      uniqueEmployees,
    };
  }, [timeEntries]);

  // Filter and sort data
  const filteredAndSortedEntries = useMemo(() => {
    let filtered = [...timeEntries];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.description.toLowerCase().includes(query) ||
          getEmployeeName(e.employeeId, e.employeeName).toLowerCase().includes(query) ||
          e.date.includes(query)
      );
    }

    // Apply employee filter
    if (employeeFilter !== 'all') {
      filtered = filtered.filter((e) => e.employeeId === Number(employeeFilter));
    }

    // Apply approval filter
    if (approvalFilter === 'approved') {
      filtered = filtered.filter((e) => e.approved);
    } else if (approvalFilter === 'pending') {
      filtered = filtered.filter((e) => !e.approved);
    }

    // Apply billable filter
    if (billableFilter === 'billable') {
      filtered = filtered.filter((e) => e.billable);
    } else if (billableFilter === 'non-billable') {
      filtered = filtered.filter((e) => !e.billable);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      if (sortField === 'date') {
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
      } else if (sortField === 'employeeId') {
        aValue = getEmployeeName(a.employeeId, a.employeeName);
        bValue = getEmployeeName(b.employeeId, b.employeeName);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [timeEntries, searchQuery, employeeFilter, approvalFilter, billableFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedEntries.length / pageSize);
  const paginatedEntries = filteredAndSortedEntries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, employeeFilter, approvalFilter, billableFilter, sortField, sortDirection, pageSize]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 inline" />
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const handleRetry = async () => {
    if (isFinOpsMode || finOpsNotConfigured) {
      const dataMode = getDataMode();
      const companyInstanceId = currentCompany?.instanceId;
      
      if (dataMode === 'live' && projectCode) {
        if (companyInstanceId) {
          setIsFinOpsMode(true);
          setLiveLoading(true);
          setLiveError(null);
          setFinOpsNotConfigured(false);
          
          try {
            const finOpsData = await fetchFinOpsTimesheets(projectCode, companyInstanceId);
            if (finOpsData) {
              const mappedEntries = finOpsData.map((entry) => 
                mapFinOpsTimesheetToTimeEntry(entry, projectId)
              );
              setLiveTimeEntries(mappedEntries);
            } else {
              setLiveTimeEntries([]);
            }
          } catch (err) {
            // FinOps API unavailable (e.g. 401 when running locally without preprod auth)
            // Fall back gracefully to local database time entries (propTimeEntries)
            console.warn('[TimeEntriesTab] FinOps API unavailable, falling back to local data:', err);
            setIsFinOpsMode(false);
            setLiveTimeEntries([]);
          } finally {
            setLiveLoading(false);
          }
        } else {
          setFinOpsNotConfigured(true);
          setLiveTimeEntries([]);
        }
      }
    } else if (onRetry) {
      onRetry();
    }
  };

  // FinOps not configured state
  if (finOpsNotConfigured) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">FinOps Integration Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  The FinOps integration has not been set up for this company. Please configure the integration in Settings to view timesheet data.
                </p>
              </div>
              <Button variant="outline" onClick={handleRetry}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Error Loading Data</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              {onRetry && <Button onClick={handleRetry}>Retry</Button>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Empty state
  if (timeEntries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Time Entries</h3>
                <p className="text-sm text-muted-foreground">
                  There are no time entries associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{summary.totalHours.toFixed(1)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.billableHours.toFixed(1)} billable, {summary.nonBillableHours.toFixed(1)} non-billable
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Billable Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.totalBillableValue)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              From {summary.billableHours.toFixed(1)} billable hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Approved Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <div className="text-2xl font-bold">{summary.approvedHours.toFixed(1)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((summary.approvedHours / summary.totalHours) * 100).toFixed(0)}% of total hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{summary.uniqueEmployees}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Logging time on this project
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Time Entries</CardTitle>
          <CardDescription>
            Time booked against this project{currentCompany ? ` from ${currentCompany.name}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search time entries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {uniqueEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={String(employee.id)}>
                      {employee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={approvalFilter} onValueChange={(value) => setApprovalFilter(value as ApprovalFilter)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by approval" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              <Select value={billableFilter} onValueChange={(value) => setBillableFilter(value as BillableFilter)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by billable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="billable">Billable</SelectItem>
                  <SelectItem value="non-billable">Non-Billable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {paginatedEntries.length} of {filteredAndSortedEntries.length} entries
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('date')}>
                    Date {getSortIcon('date')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('employeeId')}>
                    Employee {getSortIcon('employeeId')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>
                    Description {getSortIcon('description')}
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => handleSort('hours')}>
                    Hours {getSortIcon('hours')}
                  </TableHead>
                  <TableHead>Billable</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Bill Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No entries match your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedEntries.map((entry) => (
                    <TableRow key={entry.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <button
                          onClick={() => setSelectedEntry(entry)}
                          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        >
                          {format(new Date(entry.date), 'dd MMM yyyy')}
                        </button>
                      </TableCell>
                      <TableCell>{getEmployeeName(entry.employeeId, entry.employeeName)}</TableCell>
                      <TableCell className="max-w-[300px] truncate">{entry.description}</TableCell>
                      <TableCell className="text-right font-medium">{entry.hours.toFixed(1)}</TableCell>
                      <TableCell>
                        {entry.billable ? (
                          <Badge variant="default" accent="info" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Billable
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Non-Billable
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.approved ? (
                          <Badge 
                            variant="success" 
                            accent="success" 
                            className="gap-1 cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApprovalHistoryEntry(entry);
                              setApprovalHistorySheetOpen(true);
                            }}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Approved
                          </Badge>
                        ) : (
                          <Badge 
                            variant="warning" 
                            accent="warning" 
                            className="gap-1 cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApprovalHistoryEntry(entry);
                              setApprovalHistorySheetOpen(true);
                            }}
                          >
                            <Clock className="h-3 w-3" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(entry.hours * entry.costRate)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {entry.billable ? formatCurrency(entry.hours * entry.billRate) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredAndSortedEntries.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet
        open={!!selectedEntry}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedEntry(null);
          }
        }}
      >
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedEntry && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      <SheetTitle>Time Entry Details</SheetTitle>
                    </div>
                    <SheetDescription className="space-y-1">
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(selectedEntry.date), 'EEEE, dd MMMM yyyy')}
                      </div>
                      <div className="text-xs">
                        Internal ID: <span className="font-mono font-medium">{selectedEntry.id}</span>
                      </div>
                      {selectedEntry.externalTimesheetId && (
                        <div className="text-xs">
                          Timesheet ID: <span className="font-mono font-medium">{selectedEntry.externalTimesheetId}</span>
                        </div>
                      )}
                    </SheetDescription>
                  </div>
                  {selectedEntry.externalTimesheetId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="View in timesheet system"
                      onClick={() => {
                        // This would link to the external timesheet system
                        // e.g., window.open(`https://timesheet-system.com/entries/${selectedEntry.externalTimesheetId}`, '_blank')
                      }}
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                {/* Status Badges */}
                <div className="flex gap-2">
                  {selectedEntry.billable ? (
                    <Badge variant="default" accent="info" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Billable
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      Non-Billable
                    </Badge>
                  )}
                  {selectedEntry.approved ? (
                    <Badge variant="success" accent="success" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Approved
                    </Badge>
                  ) : (
                    <Badge variant="warning" accent="warning" className="gap-1">
                      <Clock className="h-3 w-3" />
                      Pending Approval
                    </Badge>
                  )}
                </div>

                {/* Key Information */}
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Employee</label>
                    <p className="mt-1 text-sm font-medium">{getEmployeeName(selectedEntry.employeeId, selectedEntry.employeeName)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="mt-1 text-sm">{selectedEntry.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Hours</label>
                      <p className="mt-1 text-lg font-semibold">{selectedEntry.hours.toFixed(1)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Date</label>
                      <p className="mt-1 text-sm">{format(new Date(selectedEntry.date), 'dd MMM yyyy')}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium mb-3">Financial Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Cost Rate</label>
                        <p className="mt-1 text-sm">{formatCurrency(selectedEntry.costRate)}/hr</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Total Cost</label>
                        <p className="mt-1 text-sm font-medium">
                          {formatCurrency(selectedEntry.hours * selectedEntry.costRate)}
                        </p>
                      </div>
                      {selectedEntry.billable && (
                        <>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Bill Rate</label>
                            <p className="mt-1 text-sm">{formatCurrency(selectedEntry.billRate)}/hr</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Bill Value</label>
                            <p className="mt-1 text-sm font-medium">
                              {formatCurrency(selectedEntry.hours * selectedEntry.billRate)}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-4 border-t">
                  <Button className="w-full" variant="outline" asChild>
                    <a
                      href={`#/timesheet/entries/${selectedEntry.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View in Timesheet System
                    </a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Approval History Sheet */}
      {approvalHistoryExpanded ? (
        <Dialog open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>
                    Approval History for Time Entry
                  </DialogTitle>
                  <DialogDescription>
                    {approvalHistoryEntry && (
                      <>
                        {format(new Date(approvalHistoryEntry.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryEntry.employeeId, approvalHistoryEntry.employeeName)}
                      </>
                    )}
                  </DialogDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setApprovalHistoryExpanded(false)}
                  title="Return to sheet view"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryEntry?.approvalHistory && (
                <ApprovalHistoryTimeline 
                  approvalHistory={approvalHistoryEntry.approvalHistory}
                  timeEntryId={String(approvalHistoryEntry.id)}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>
                    Approval History for Time Entry
                  </SheetTitle>
                  <SheetDescription>
                    {approvalHistoryEntry && (
                      <>
                        {format(new Date(approvalHistoryEntry.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryEntry.employeeId, approvalHistoryEntry.employeeName)}
                      </>
                    )}
                  </SheetDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setApprovalHistoryExpanded(true)}
                  title="Expand to full view"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryEntry?.approvalHistory && (
                <ApprovalHistoryTimeline 
                  approvalHistory={approvalHistoryEntry.approvalHistory}
                  timeEntryId={String(approvalHistoryEntry.id)}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
